import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { AppBar, Box, Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../assets/Logo.webp';
import { SiteContainer } from '../styles/primitives';

const navigationItems = [
  { to: '/', label: 'Home' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const HeaderBar = styled(AppBar)`
  && {
    background: ${({ $scrolled, theme }) => ($scrolled ? 'rgba(255, 250, 245, 0.92)' : 'rgba(255, 250, 245, 0.82)')};
    backdrop-filter: blur(18px);
    box-shadow: ${({ $scrolled, theme }) => ($scrolled ? theme.shadows.sm : 'none')};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const HeaderInner = styled(SiteContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.lg};
  min-height: ${({ $scrolled, theme }) => ($scrolled ? '64px' : '84px')};
  transition: min-height 0.24s ease;

  ${({ theme }) => theme.media.md} {
    min-height: ${({ $scrolled, theme }) => ($scrolled ? '72px' : '104px')};
  }
`;

const BrandLink = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const BrandMark = styled.img`
  width: ${({ $scrolled }) => ($scrolled ? 'clamp(4.2rem, 8vw, 5.25rem)' : 'clamp(5.45rem, 11vw, 7.5rem)')};
  height: auto;
  transition: width 0.24s ease;
`;

const DesktopNav = styled.nav`
  display: none;
  align-items: center;
  gap: clamp(0.85rem, 1.8vw, 1.75rem);

  ${({ theme }) => theme.media.md} {
    display: flex;
  }
`;

const navLinkActiveStyles = {
  color: '#8f4b35',
};

const StyledNavLink = styled(NavLink)`
  position: relative;
  padding-block: 0.4rem;
  font-size: ${({ $scrolled, theme }) => ($scrolled ? '0.82rem' : '0.99rem')};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: color 0.2s ease, font-size 0.24s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -0.1rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent};
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.2s ease;
  }

  &:hover,
  &:focus-visible,
  &.active {
    color: ${({ theme }) => theme.colors.text};
  }

  &:hover::after,
  &:focus-visible::after,
  &.active::after {
    transform: scaleX(1);
  }
`;

const MobileMenuButton = styled(IconButton)`
  && {
    display: inline-flex;
    width: ${({ $scrolled }) => ($scrolled ? '2.7rem' : '2.85rem')};
    height: ${({ $scrolled }) => ($scrolled ? '2.7rem' : '2.85rem')};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.surface};
    transition: width 0.24s ease, height 0.24s ease;
  }

  ${({ theme }) => theme.media.md} {
    && {
      display: none;
    }
  }
`;

const DrawerContent = styled(Box)`
  width: min(86vw, 22rem);
  height: 100%;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const DrawerNav = styled.nav`
  display: grid;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DrawerLink = styled(NavLink)`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.radii.md};
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible,
  &.active {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.accentStrong};
  }
`;

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 12);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeaderBar position="fixed" elevation={0} $scrolled={scrolled}>
      <HeaderInner $scrolled={scrolled}>
        <BrandLink to="/" aria-label="Michelle Hunt home">
          <BrandMark src={logo} alt="Michelle Hunt logo" $scrolled={scrolled} />
        </BrandLink>

        <DesktopNav aria-label="Primary navigation">
          {navigationItems.map((item) => (
            <StyledNavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
              style={({ isActive }) => (isActive ? navLinkActiveStyles : undefined)}
              $scrolled={scrolled}
            >
              {item.label}
            </StyledNavLink>
          ))}
        </DesktopNav>

        <MobileMenuButton
          aria-label="Open navigation menu"
          onClick={() => setDrawerOpen(true)}
          $scrolled={scrolled}
        >
          <MenuIcon />
        </MobileMenuButton>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <DrawerContent role="presentation">
            <DrawerHeader>
              <BrandLink to="/" aria-label="Michelle Hunt home" onClick={() => setDrawerOpen(false)}>
                <BrandMark src={logo} alt="Michelle Hunt logo" />
              </BrandLink>
              <IconButton
                aria-label="Close navigation menu"
                onClick={() => setDrawerOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </DrawerHeader>
            <DrawerNav aria-label="Mobile navigation">
              {navigationItems.map((item) => (
                <DrawerLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => (isActive ? 'active' : undefined)}
                  onClick={() => setDrawerOpen(false)}
                >
                  {item.label}
                </DrawerLink>
              ))}
            </DrawerNav>
          </DrawerContent>
        </Drawer>
      </HeaderInner>
    </HeaderBar>
  );
}

export default Header;
